import type { CSSProperties, ReactNode } from "react";

import { Bone } from "./Bone";
import emptyStyles from "./EmptyState.module.css";
import styles from "./PageSkeleton.module.css";

const NBSP = "\u00a0";

/**
 * A page's own geometry while it loads under the Vorgang's frame (Task 10;
 * UX review 26.10.2026, B-4): the frame stands with the Vorgang's name, and
 * the page under it is drawn in bones where its parts will stand — never a
 * line „… wird geladen …", which flashed up in the middle of a tab change and
 * showed the technique instead of a movement. Each screen lays out its bones
 * with its own stylesheet's classes, so the answer lands where the bones
 * were (as `RecordSkeleton` and `EingangSkeleton` do).
 *
 * The region says it is busy; `label` is its name for a screen reader, not
 * text on the page. Errors are not this: they stay `StateMessage`s.
 */
export function Skeleton({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={className} role="region" aria-label={label} aria-busy="true">
      {children}
    </div>
  );
}

/**
 * One line of text in the line box of the text it stands for: the line
 * carries that text's own class and a non-breaking space, so its height *is*
 * that text's height, and the bone lies over it — no pixel figure measured
 * off a loaded page that would drift with the next change of a token.
 */
export function BoneLine({ className, width, height }: { className?: string; width: string; height?: string }) {
  return (
    <p className={[styles.line, className].filter(Boolean).join(" ")} aria-hidden="true">
      {NBSP}
      <Bone width={width} height={height} />
    </p>
  );
}

/**
 * A screen's own title as it stands in the frame (`PageTitle`): the line box
 * of the section heading — the UI face at `--text-lg`, line height 1.3, as
 * `PageTitle.module.css` sets it.
 */
export function BoneTitle({ className, width = "9rem" }: { className?: string; width?: string }) {
  return <BoneLine className={[styles.title, className].filter(Boolean).join(" ")} width={width} height="1rem" />;
}

/**
 * A floating surface in the recipe of `components/Panel` (design 2026-10-24
 * §4.2) — a panel whose head and rows are bones. `head` draws the panel's
 * head: its title and its hint line over a hairline.
 */
export function BonePanel({
  head = false,
  className,
  style,
  children,
}: {
  head?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  return (
    <div className={[styles.panel, className].filter(Boolean).join(" ")} style={style} aria-hidden="true">
      {head ? (
        <div className={styles.panelHead}>
          <BoneLine className={styles.panelTitle} width="7rem" />
          <BoneLine className={styles.panelHint} width="22rem" />
        </div>
      ) : null}
      {children !== undefined ? <div className={styles.panelBody}>{children}</div> : null}
    </div>
  );
}

/**
 * An empty state's place (`components/EmptyState`): the floating surface at
 * its height, the mark and the lines centred in it — where a page waits for
 * the answer that decides which empty state it shows.
 */
export function BoneEmptyState() {
  return (
    <div className={emptyStyles.empty} aria-hidden="true">
      <div className={emptyStyles.inner}>
        <Bone width="2.5rem" height="2.5rem" />
        <div className={`${emptyStyles.text} ${styles.emptyText}`}>
          <BoneLine className={emptyStyles.title} width="12rem" />
          <BoneLine className={emptyStyles.body} width="28rem" />
          <BoneLine className={emptyStyles.body} width="20rem" />
        </div>
      </div>
    </div>
  );
}
